// Création d'une délégation (D-021) : le délégant est l'utilisateur connecté ; il ne délègue
// que valider, décider ou signer sur les domaines où il détient ces pouvoirs.
import { TYPES_DELEGATION } from '@cc/contrats';
import { Bandeau, Bouton, CaseACocher, Champ, DateChamp, Formulaire, GrilleChamps, Selection, useBrouillon } from '@cc/ui';
import { useMemo, useState } from 'react';
import { requete } from '../../api/client';
import { useUtilisateurs } from '../../api/requetes';
import type { Delegation } from '../../api/types';
import { versIso } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { LIBELLES_DOMAINES, peut } from '../../session/permissions';
import styles from './administration.module.css';
import { nomUtilisateur } from './PageUtilisateurs';

export const LIBELLES_TYPES: Record<string, string> = {
  signature: 'Délégation de signature',
  fonction: 'Délégation de fonction',
  interim: 'Intérim',
};

const POUVOIRS: Record<string, string[]> = { signature: ['signer'], fonction: ['valider', 'decider', 'signer'], interim: ['valider', 'decider', 'signer'] };

interface Saisie {
  type: string;
  delegataire: string;
  domaines: string[];
  plafond: string;
  debut: string;
  fin: string;
}

export function FormulaireDelegation({ onTermine }: { onTermine: () => void }) {
  const { contexte } = useSession();
  const utilisateurs = useUtilisateurs();
  const listeLisible = peut(contexte, 'utilisateurs', 'voir');
  const [s, setS] = useState<Saisie>({ type: 'signature', delegataire: '', domaines: [], plafond: '', debut: '', fin: '' });
  const brouillon = useBrouillon('delegation-nouvelle', s);

  // Domaines où l'utilisateur détient en propre au moins un pouvoir délégable de ce type.
  const domaines = useMemo(() => {
    const permissions = contexte?.appartenance_active?.permissions ?? {};
    return Object.entries(permissions)
      .filter(([, ps]) => ps.some((p) => POUVOIRS[s.type]?.includes(p)))
      .map(([d]) => d);
  }, [contexte, s.type]);

  const creation = useAction({
    executer: () =>
      requete<Delegation>('/delegations', {
        methode: 'POST',
        corps: {
          type: s.type,
          delegataire_appartenance_id: s.delegataire.trim(),
          domaines: s.domaines,
          plafond_montant: s.plafond === '' ? null : Number(s.plafond),
          debut: s.debut ? versIso(s.debut) : '',
          fin: s.fin ? versIso(s.fin) : '',
        },
      }),
    succes: 'Délégation enregistrée.',
    invalider: [['delegations'], ['contexte']],
    apres: () => {
      brouillon.effacer();
      onTermine();
    },
  });

  const ids: Record<string, string> = { type: 'del-type', delegataire_appartenance_id: 'del-delegataire', domaines: 'del-domaines', plafond_montant: 'del-plafond', debut: 'del-debut', fin: 'del-fin' };
  const erreur = (c: string) => creation.erreursChamps.find((e) => e.champ === c || e.champ.startsWith(`${c}.`))?.message ?? null;
  const maj = (cle: keyof Saisie) => (e: { target: { value: string } }) => setS((x) => ({ ...x, [cle]: e.target.value }));

  return (
    <Formulaire
      libelle="Nouvelle délégation"
      onSoumettre={() => creation.lancer(undefined)}
      erreurs={creation.erreursChamps.map((e) => ({ champ: ids[e.champ.split('.')[0] ?? ''] ?? 'del-type', message: e.message }))}
      brouillonEnregistreLe={brouillon.enregistreLe}
      restauration={
        brouillon.brouillonDisponible
          ? { date: brouillon.brouillonDate, onReprendre: () => { setS(brouillon.brouillonDisponible as Saisie); brouillon.ignorer(); }, onIgnorer: brouillon.effacer }
          : null
      }
      actions={
        <>
          <Bouton type="submit" chargement={creation.enCours}>
            Enregistrer la délégation
          </Bouton>
          <Bouton variante="secondaire" onClick={onTermine}>
            Annuler
          </Bouton>
        </>
      }
    >
      {creation.messageErreur && creation.erreursChamps.length === 0 ? <Bandeau type="erreur">{creation.messageErreur}</Bandeau> : null}
      <GrilleChamps>
        <Selection
          id="del-type"
          libelle="Type"
          value={s.type}
          onChange={(e) => setS((x) => ({ ...x, type: e.target.value, domaines: [] }))}
          options={TYPES_DELEGATION.map((t) => ({ valeur: t, libelle: LIBELLES_TYPES[t] ?? t }))}
          aide={s.type === 'signature' ? 'Transmet le pouvoir de signer.' : 'Transmet valider, décider et signer, dans la limite de vos propres pouvoirs.'}
          required
        />
        {listeLisible ? (
          <Selection
            id="del-delegataire"
            libelle="Délégataire"
            optionVide="Choisir une personne"
            value={s.delegataire}
            onChange={maj('delegataire')}
            options={(utilisateurs.data ?? [])
              .filter((u) => u.statut_appartenance === 'active' && u.appartenance_id !== contexte?.appartenance_active?.id)
              .map((u) => ({ valeur: u.appartenance_id, libelle: nomUtilisateur(u) }))}
            erreur={erreur('delegataire_appartenance_id')}
            required
          />
        ) : (
          <Champ
            id="del-delegataire"
            libelle="Identifiant d'appartenance du délégataire"
            aide="Votre rôle ne permet pas de consulter la liste des agents : l'administrateur local vous communique cet identifiant."
            value={s.delegataire}
            onChange={maj('delegataire')}
            erreur={erreur('delegataire_appartenance_id')}
            required
          />
        )}
        <DateChamp id="del-debut" libelle="Début" avecHeure value={s.debut} onChange={maj('debut')} erreur={erreur('debut')} required />
        <DateChamp id="del-fin" libelle="Fin" avecHeure value={s.fin} onChange={maj('fin')} erreur={erreur('fin')} required />
        <Champ id="del-plafond" libelle="Plafond (francs CFA)" type="number" min={0} step={1} value={s.plafond} onChange={maj('plafond')} erreur={erreur('plafond_montant')} facultatif aide="Montant au-delà duquel la délégation ne s'applique pas" />
      </GrilleChamps>
      <fieldset id="del-domaines" className={styles.matrice}>
        <legend>Périmètre (domaines)</legend>
        {erreur('domaines') ? <Bandeau type="erreur">{erreur('domaines')}</Bandeau> : null}
        {domaines.length === 0 ? (
          <p>Vous ne détenez aucun pouvoir délégable de ce type dans cet espace.</p>
        ) : (
          <div className={styles.cases}>
            {domaines.map((d) => (
              <CaseACocher
                key={d}
                libelle={LIBELLES_DOMAINES[d] ?? d}
                checked={s.domaines.includes(d)}
                onChange={() => setS((x) => ({ ...x, domaines: x.domaines.includes(d) ? x.domaines.filter((y) => y !== d) : [...x.domaines, d] }))}
              />
            ))}
          </div>
        )}
      </fieldset>
    </Formulaire>
  );
}
